/**
 * The one clipboard write for the designer.
 *
 * Prefers the async Clipboard API (`navigator.clipboard.writeText`) and falls back to the
 * classic hidden-textarea + `document.execCommand("copy")` trick when the Clipboard API is
 * unavailable (older browsers, some embedded webviews) or refused at runtime (an insecure
 * context, or a permission prompt the user dismissed).
 *
 * Faz 3 moves the designer's bare `navigator.clipboard.writeText(...)` call sites onto this
 * helper so every copy path gets the same fallback and focus-restoration behavior instead of
 * silently doing nothing when the Clipboard API rejects.
 *
 * @param text the plain text to copy
 * @returns whether the copy succeeded
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Clipboard permission refused, or an insecure context: fall through to the textarea trick.
    }
  }

  const previouslyFocused: Element | null = document.activeElement;

  const textarea: HTMLTextAreaElement = document.createElement("textarea");
  textarea.readOnly = true;
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();

  let succeeded: boolean = false;
  try {
    succeeded = document.execCommand("copy");
  } catch {
    succeeded = false;
  }

  document.body.removeChild(textarea);

  if (previouslyFocused instanceof HTMLElement) {
    previouslyFocused.focus();
  }

  return succeeded;
};
